import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserStore } from '../stores/userStore';

interface AuthenticatedRouteProps {
  children: React.ReactNode;
}

const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({
  children,
}) => {
  const location = useLocation();
  const { isAuthenticated, hasToken, isInitialized, isLoading, init } = useUserStore();
  const [hydrated, setHydrated] = useState(
    useUserStore.persist.hasHydrated()
  );

  // Wait for Zustand persist to hydrate before calling init().
  // Without this, init() reads default (empty) state and concludes there is
  // no session, redirecting the user to sign-in on every page reload.
  useEffect(() => {
    if (hydrated) return;
    const unsub = useUserStore.persist.onFinishHydration(() => {
      setHydrated(true);
    });
    return unsub;
  }, [hydrated]);

  useEffect(() => {
    if (hydrated) {
      init();
    }
  }, [hydrated, init]);

  // Show spinner while Zustand is hydrating or init hasn't finished
  if (!hydrated || !isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated && !hasToken) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?redirect=${returnUrl}`} replace />;
  }

  return <>{children}</>;
};

export default AuthenticatedRoute;
